import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GenericResourceService } from '@mri/app/resources';
import { Device, Hardware, Mobile, SensorDataHardware } from '@mri/types';
import { map, mergeMap, Observable, of, Subscription, tap } from 'rxjs';
import { CardComponent } from '../../@theme/components/card/card.component';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FieldExpressionModule } from '../inputs/field-expression';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-mobile-sensors-config',
  templateUrl: './mobile-sensors-config.component.html',
  standalone: true,
  imports: [
    CardComponent,
    MatButtonModule,
    MatSlideToggleModule,
    FieldExpressionModule
  ],
  styleUrl: './mobile-sensors-config.component.scss'
})
export class MobileSensorsConfigComponent implements OnInit, OnDestroy {

  private _subscription = new Subscription();

  inputOptionsUrl = 'positions/attributes/choice';
  sensors: SensorDataHardware[] = [];
  isSubmitting: boolean;
  hardware?: Hardware;
  device?: Device;

  @Input()
  mobile?: Mobile;

  @Input()
  mobileId: string;

  constructor(
    private _genericSrcSrv: GenericResourceService,
    private _snackbar: MatSnackBar
  ) {
  }

  ngOnInit(): void {
    this._subscription
      .add(
        of(this.mobileId)
          .pipe(
            mergeMap(() => this._brigMobile()),
            mergeMap(() => this._brigDevice()),
            mergeMap(() => this._brigHardware()),
            mergeMap(() => this._brigSensors()),
            tap(() => this._organizeData())
          )
          .subscribe()
      );

  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  toggleEnable(sensor: SensorDataHardware) {
    if (sensor.config.enable) this.disableEnable(sensor);
    else this.activeEnable(sensor);
  }

  activeEnable(sensor: SensorDataHardware) {
    Object.assign(sensor.config, { enable: true });
  }

  disableEnable(sensor: SensorDataHardware) {
    Object.assign(sensor.config, { enable: false });
  }

  onSubmit() {
    const mobile = this.mobile;

    if (!mobile) return;

    const sensorConfig = this.sensors
      .filter(s => !s.config.enable || (s.config.enable && s.config.edit && s.config.expression?.input))
      .map(s => ({ ...(s.config || {}), key: s.key }));

    if(!mobile.config ) mobile.config = { sensors: [], timezone: '+05:00' };

    mobile.config.sensors = sensorConfig;
    this.isSubmitting = true;

    this._subscription.add(
      this._genericSrcSrv
        .putElement<Mobile>(`mobiles/${this.mobile?._id}`, mobile)
        .pipe(
          tap(() => this.isSubmitting = false),
          tap(() => this._snackbar.open('Configuracion guardada correctamente!'))
        ).subscribe()
    );
  }

  private _brigMobile(): Observable<Mobile | null> {
    if (this.mobile) return of(this.mobile);

    if (!this.mobileId) return of(null);

    return this._genericSrcSrv
      .getElement<Mobile>('mobiles', this.mobileId)
      .pipe(
        map(({ object }) => object),
        tap(item => this.mobile = item),
        tap(() => {
          if (!this.mobile) return;
          this.mobile.config = this.mobile?.config || {};
        })
      );
  }

  private _brigDevice(): Observable<Device | null> {
    if (!this.mobile?.deviceId) return of(null);

    return this._genericSrcSrv
      .getElement<Device>('devices', this.mobile?.deviceId)
      .pipe(
        map(({ object }) => object),
        tap(item => this.device = item)
      );
  }

  private _brigHardware(): Observable<Hardware | null> {
    if (!this.device?.hardwareId) return of(null);

    return this._genericSrcSrv
      .getElement<Hardware>('hardware', this.device?.hardwareId)
      .pipe(
        map(({ object }) => object),
        tap(item => this.hardware = item)
      );
  }

  private _organizeData() {
    const sensorConfigs = this.mobile?.config?.sensors;

    this.sensors = this.sensors
      .filter(s => this.hardware?.config?.sensors?.find(s1 => s1.key === s.key))
      .map(sensor => {
        const hSensor = this.hardware?.config?.sensors?.find(s1 => s1.key === sensor.key);

        const config = sensorConfigs?.find(s => s.key === sensor.key);

        sensor.config = config || {
          key: sensor.key, enable: true,
          expression: { type: 'simple', input: '' },
          editable: hSensor?.editable
        };

        return sensor;
      });
  }

  private _brigSensors(): Observable<Array<SensorDataHardware>> {
    return this._genericSrcSrv
      .getElements<SensorDataHardware>(
        'sensors',
        { paginate: { page: 1, size: 99 } }
      )
      .pipe(
        map(({ objects }) => objects),
        tap(sensors => this.sensors = sensors)
      );
  }

}
